import { useEffect, useState } from "react";
import { useNavigate } from "@/lib/router-compat";
import { Button } from "@/components/ui/button";
import { ShoppingCart, Loader2, Check } from "lucide-react";
import { usePartsCart } from "@/context/PartsCartContext";
import { useToast } from "@/hooks/use-toast";

interface Props {
  productId: string;
  disabled?: boolean;
  size?: "default" | "sm" | "lg";
  className?: string;
  showQty?: boolean;
  maxQty?: number;
}

export default function AddToCartButton({ productId, disabled, size = "lg", className, showQty, maxQty }: Props) {
  const hardCap = Math.min(99, Math.max(0, Math.trunc(maxQty ?? 99)));
  const [qty, setQty] = useState(() => (hardCap > 0 ? 1 : 0));
  useEffect(() => {
    if (hardCap <= 0) {
      setQty(0);
    } else if (qty > hardCap) {
      setQty(hardCap);
    } else if (qty < 1) {
      setQty(1);
    }
  }, [hardCap, qty]);
  const [busy, setBusy] = useState(false);
  const [added, setAdded] = useState(false);
  const { addItem } = usePartsCart();
  const { toast } = useToast();
  const navigate = useNavigate();

  async function onAdd() {
    if (disabled || busy || hardCap <= 0) return;
    setBusy(true);
    try {
      await addItem(productId, Math.min(qty, hardCap));
      setAdded(true);
      toast({ title: "Added to cart" });
      setTimeout(() => setAdded(false), 1800);
    } catch (e: any) {
      toast({ title: "Couldn't add to cart", description: e?.message || "Try again", variant: "destructive" });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={`flex flex-wrap items-center gap-2 ${className || ""}`}>
      {showQty && (
        <div className="inline-flex items-center rounded-md border bg-background">
          <button type="button" className="px-3 py-2 text-sm" onClick={() => setQty((q) => Math.max(1, q - 1))} disabled={busy}>−</button>
          <span className="w-10 text-center text-sm tabular-nums">{qty}</span>
          <button
            type="button"
            className="px-3 py-2 text-sm disabled:opacity-40"
            onClick={() => setQty((q) => Math.min(hardCap, q + 1))}
            disabled={busy || qty >= hardCap}
          >
            +
          </button>
        </div>
      )}
      <Button size={size} onClick={onAdd} disabled={disabled || busy || hardCap <= 0}>
        {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : added ? <Check className="w-4 h-4 mr-2" /> : <ShoppingCart className="w-4 h-4 mr-2" />}
        {disabled || hardCap <= 0 ? "Out of stock" : added ? "Added" : "Add to cart"}
      </Button>
      {showQty && hardCap > 0 && hardCap < 99 && (
        <span className="text-xs text-muted-foreground w-full sm:w-auto">Max {hardCap} available</span>
      )}
      <Button size={size} variant="secondary" onClick={() => navigate("/parts-catalog/cart")}>
        View cart
      </Button>
    </div>
  );
}